import {yupResolver} from "@hookform/resolvers/yup";
import {KeyboardEventHandler, useEffect, useMemo} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {FiSend, FiSquare} from "react-icons/fi";
import * as Yup from "yup";

import {TextAreaField} from "@/components/form/fields/TextAreaField.tsx";
import {IconButton} from "@/components/iconButton/IconButton.tsx";
import {ANALYZER_REVISION_MAX_LENGTH} from "@/pages/analyzer/Analyzer.constants.ts";
import {AnalyzerPhase, RevisionFormValues} from "@/pages/analyzer/Analyzer.types.ts";
import {formatCost, getLatestCheckSelectionEvent} from "@/pages/analyzer/Analyzer.utils.ts";
import {useAnalyzerContext} from "@/pages/analyzer/hooks/AnalyzerContext.tsx";

import styles from "./AnalyzerChatInput.module.scss";

const RevisionSchema = Yup.object().shape({
  revision: Yup.string()
    .defined()
    .trim()
    .max(ANALYZER_REVISION_MAX_LENGTH, `Please keep your request under ${ANALYZER_REVISION_MAX_LENGTH} characters`),
});

export const AnalyzerChatInput = () => {
  const {events, phase, abort, handleRevision, showDebug, usage, isCreating} = useAnalyzerContext();
  const selectionEvent = useMemo(() => getLatestCheckSelectionEvent(events), [events]);

  const methods = useForm<RevisionFormValues>({
    resolver: yupResolver<RevisionFormValues>(RevisionSchema),
    defaultValues: {revision: ""},
  });
  const isStreaming = phase === AnalyzerPhase.STREAMING;
  const canRevise = (phase === AnalyzerPhase.CHECK_SELECTION || phase === AnalyzerPhase.DONE) && !isCreating;
  const revisionText = methods.watch("revision");
  const rows = Math.min(5, revisionText.split("\n").length); // Fallback auto-grow for browsers without field-sizing: content
  const onRevise = methods.handleSubmit(({revision}) => {
    if (!revision?.trim()) return;
    handleRevision(revision);
    methods.reset();
  });
  const onKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = (event) => {
    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      onRevise();
    }
  };

  useEffect(() => {
    methods.reset();
  }, [selectionEvent, methods]);

  return (
    <div className={styles.inputBar}>
      {showDebug && usage && (
        <div className={styles.debugFooter}>
          {usage.input_tokens.toLocaleString()} in / {usage.output_tokens.toLocaleString()} out
          {!!(usage.cache_read_tokens || usage.cache_write_tokens) && (
            <>
              {" "}
              · cache {(usage.cache_read_tokens ?? 0).toLocaleString()} read /{" "}
              {(usage.cache_write_tokens ?? 0).toLocaleString()} write
            </>
          )}
          {usage.pricing && <> · {formatCost(usage)}</>}
          {usage.elapsed > 0 && <div>Completed in {usage.elapsed.toFixed(1)}s</div>}
        </div>
      )}
      <FormProvider {...methods}>
        <form onSubmit={onRevise} className={styles.inputRow}>
          <TextAreaField
            name="revision"
            rows={rows}
            maxLength={ANALYZER_REVISION_MAX_LENGTH}
            disabled={!canRevise}
            placeholder="Write a message..."
            onKeyDown={onKeyDown}
            className={styles.textarea}
            endElement={
              isStreaming ? (
                <IconButton
                  icon={FiSquare}
                  variant="ghost"
                  size="fitIcon"
                  className={styles.inlineButton}
                  aria-label="Stop analysis"
                  title="Stop analysis"
                  onClick={abort}
                />
              ) : (
                <IconButton
                  icon={FiSend}
                  type="submit"
                  variant="ghost"
                  size="fitIcon"
                  className={styles.inlineButton}
                  aria-label="Send message"
                  title="Send message"
                  disabled={!canRevise || !revisionText.trim()}
                />
              )
            }
          />
        </form>
      </FormProvider>
    </div>
  );
};
